import { useEffect, useState } from "react";
import { Gift, Link2, Lock, Sparkles, Feather, BookHeart } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AdventParticipationForm from "@/components/AdventParticipationForm";
import { adventOpenDay, ADVENT_PRIZES } from "@/lib/advent";
import teaserAsset from "@/assets/adventkalender-teaser.webp.asset.json";

type Door = { day: number; kind: string; title: string; body: string; prize: string | null };

const ORDER = [7, 14, 3, 20, 11, 1, 18, 9, 24, 5, 16, 12, 22, 2, 13, 8, 19, 6, 23, 15, 4, 10, 21, 17];

const Adventkalender = () => {
  useSeo({
    title: "Adventkalender – Impulse, Hoffnungsgeschichten & Gewinnspiel | PRAXIS IB",
    description: "24 Türchen mit Impulsen zum Thema Nie-Aufgeben, Hoffnungsgeschichten und einem Gewinnspiel mit Manuka-Honig, Gutscheinen und einem Mikrobiomtest.",
    canonical: "/adventkalender",
  });
  const [doors, setDoors] = useState<Record<number, Door>>({});
  const [open, setOpen] = useState<Door | null>(null);
  const today = adventOpenDay(new Date());

  const copyLink = async () => {
    const url = `${window.location.origin}/adventkalender`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert – danke fürs Weitersagen! ❤️");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Link kopiert – danke fürs Weitersagen! ❤️");
      } catch {
        window.prompt("Link zum Teilen:", url);
      }
      ta.remove();
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem("advent_viewed")) {
      sessionStorage.setItem("advent_viewed", "1");
      supabase.rpc("track_advent_view").then(() => {});
    }
  }, []);

  useEffect(() => {
    supabase.from("advent_doors").select("day,kind,title,body,prize").then(({ data }) => {
      const m: Record<number, Door> = {};
      (data ?? []).forEach((d) => (m[d.day] = d));
      setDoors(m);
    });
  }, []);

  const Icon = (k: string) => (k === "geschenk" ? Gift : k === "geschichte" ? BookHeart : Feather);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24">
        <section className="py-12 md:py-16 bg-secondary">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <img
              src={teaserAsset.url}
              alt="Adventkalender von PRAXIS IB – tolle Gewinnse warten auf dich: 5× das Buch Seelenbalance, 2× Shop-Apotheke-Gutschein, 2× Manuka-Honig und als Hauptpreis ein Erstgespräch mit Mikrobiomtest"
              width={576}
              height={576}
              fetchPriority="high"
              className="mx-auto mb-6 md:mb-8 w-full max-w-[320px] sm:max-w-sm md:max-w-md h-auto rounded-3xl shadow-lg"
            />
            <p className="text-primary font-medium mb-2 flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" />Adventkalender</p>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Nie aufgeben – 24 Türchen voller Hoffnung</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Jeden Tag im Dezember öffnet sich ein Türchen: mit Impulsen zum Nachdenken, Hoffnungsgeschichten und elf Geschenken, die am 24. Dezember unter allen Teilnehmenden verlost werden.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {ORDER.map((day) => {
                const door = doors[day];
                const unlocked = day <= today && door;
                const I = door ? Icon(door.kind) : Lock;
                return (
                  <Button
                    key={day}
                    disabled={!unlocked}
                    onClick={() => door && setOpen(door)}
                    aria-label={`Türchen ${day}`}
                    variant="outline"
                    className={`relative h-auto aspect-square rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all ${
                      unlocked
                        ? "bg-gradient-to-b from-card to-accent/15 border-primary/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/25 text-foreground"
                        : "bg-gradient-to-b from-muted/40 to-accent/10 border-border text-muted-foreground cursor-not-allowed"
                    } ${day === today ? "ring-2 ring-primary shadow-lg shadow-accent/20" : ""}`}
                  >
                    <span className="text-2xl md:text-3xl font-bold">{day}</span>
                    {unlocked ? <I className={`w-4 h-4 ${door.prize ? "text-accent" : "text-primary"}`} /> : <Lock className="w-3.5 h-3.5" />}
                    <Sparkles className={`absolute top-2 right-2 w-2.5 h-2.5 ${unlocked ? "text-accent/80" : "text-accent/40"}`} aria-hidden="true" />
                  </Button>
                );
              })}
            </div>
            {today === 0 && <p className="text-center text-sm text-muted-foreground mt-6">Das erste Türchen öffnet sich am 1. Dezember.</p>}
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="rounded-3xl bg-secondary border border-primary/15 p-8 md:p-10 text-center space-y-4">
              <Sparkles className="w-5 h-5 text-accent mx-auto" aria-hidden="true" />
              <p className="text-xl md:text-2xl font-semibold text-foreground text-balance">Hoffnung wird größer, wenn wir sie teilen. ❤️</p>
              <p className="text-muted-foreground">Kennst du jemanden, der gerade ein bisschen Zuversicht gebrauchen könnte?</p>
              <p className="text-muted-foreground">Dann teile diesen Adventkalender und schenke einem lieben Menschen jeden Tag einen kleinen Lichtblick.</p>
              <p className="text-sm font-medium text-primary">Gemeinsam bringen wir mehr Hoffnung in die Adventzeit.</p>
              <Button onClick={copyLink} className="mt-2">
                <Link2 className="w-4 h-4" /> Link kopieren
              </Button>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-secondary">
          <div className="container mx-auto px-4 max-w-5xl grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Das gibt es zu gewinnen</h2>
              <ul className="space-y-2">
                {ADVENT_PRIZES.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-foreground"><Gift className="w-4 h-4 text-primary mt-1 shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">In den Lostopf</h2>
              <p className="text-sm text-muted-foreground">Öffne jeden Tag das aktuelle Türchen und trag dich dort ein – das Los gibt es nur an diesem Tag. Je öfter du dabei bist, desto mehr Lose hast du im gemeinsamen Lostopf – bis zu 24. Auch bei Impulsen und Hoffnungsgeschichten kannst du teilnehmen.</p>
              <p className="text-sm font-medium text-foreground">Start: 1. Dezember – das erste Türchen öffnet um Mitternacht.</p>
            </div>
          </div>
        </section>

        <section id="teilnahmebedingungen" className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-3xl text-sm text-muted-foreground space-y-2">
            <h2 className="text-lg font-semibold text-foreground">Teilnahmebedingungen</h2>
            <p>Veranstalterin ist PRAXIS IB, Isabella Bartak, Wien. Teilnahme von 1. bis 24. Dezember, 12:00 Uhr (Wiener Zeit), kostenlos und ohne Kaufverpflichtung; teilnahmeberechtigt sind Personen ab 18 Jahren. Pro Person ist je Türchen eine Teilnahme möglich, und zwar nur an dem Tag, an dem sich das Türchen öffnet (Wiener Zeit); insgesamt höchstens 24 Lose. Eine nachträgliche Teilnahme für vergangene Türchen ist nicht möglich, deren Inhalte bleiben aber zum Nachlesen offen. Bitte verwende immer dieselbe E-Mail-Adresse; mehrere E-Mail-Adressen zur Umgehung dieser Begrenzung sind nicht zulässig.</p>
            <p>Die elf Preise werden nach Teilnahmeschluss zufällig aus allen Losen gezogen. Jedes Los hat die gleiche Chance; mehr Teilnahmen erhöhen die Gewinnchance. Jede Person kann höchstens einen Preis gewinnen; nach einem Gewinn werden ihre übrigen Lose bei weiteren Ziehungen nicht mehr berücksichtigt. Gewinnerinnen und Gewinner werden per E-Mail benachrichtigt. Eine Barablöse ist nicht möglich, der Rechtsweg ist ausgeschlossen.</p>
            <p>Name, E-Mail-Adresse und Postadresse werden ausschließlich für das Gewinnspiel, die Benachrichtigung und den Versand verwendet, sind nicht öffentlich einsehbar und werden spätestens drei Monate nach der Verlosung gelöscht. Ein Widerruf ist jederzeit per E-Mail an office@ibpraxis.net möglich.</p>
          </div>
        </section>
        <Footer />
      </main>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader>
                <p className="text-primary text-sm font-medium">Türchen {open.day} · {open.kind === "geschenk" ? "Geschenk" : open.kind === "geschichte" ? "Hoffnungsgeschichte" : "Impuls"}</p>
                <DialogTitle className="text-2xl">{open.title}</DialogTitle>
                <DialogDescription className="text-base text-foreground leading-relaxed pt-2">{open.body}</DialogDescription>
              </DialogHeader>
              {open.prize && (
                <div className="rounded-xl bg-secondary p-4 text-sm">
                  <p className="font-semibold text-foreground flex items-center gap-2"><Gift className="w-4 h-4 text-primary" />{open.prize}</p>
                </div>
              )}
              {open.day === today ? (
                <AdventParticipationForm key={open.day} day={open.day} onViewTerms={() => { setOpen(null); setTimeout(() => document.getElementById("teilnahmebedingungen")?.scrollIntoView({ behavior: "smooth" }), 150); }} />
              ) : (
                <p className="border-t pt-5 text-sm text-muted-foreground">Das Los für dieses Türchen gab es nur am {open.day}. Dezember. Dein heutiges Los findest du hinter Türchen {today}.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Adventkalender;
